import { Button, Input } from "@fluentui/react-components";
import { ArrowClockwiseRegular, ArrowRightRegular, SearchRegular, DismissRegular } from "@fluentui/react-icons";
import { Link, useSearchParams } from "react-router-dom";
import { useFunctionContext } from "../contexts/FunctionContext";
import { PageHeading } from "../components/PageHeading";
import { PageState } from "../components/PageState";
import { CompactLinkList } from "../components/CompactLinkList";
import { ViewControls } from "../components/ViewControls";
import { useViewPreferences } from "../contexts/ViewPreferencesContext";
import { categories, getFunctionCategory } from "../utils/functionCatalog";
import "./Home.css";

export const Home = () => {
    const { view } = useViewPreferences();
    const { getFilteredFunctions, isLoading, error, refreshFunctions } = useFunctionContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get("q") || "";
    const category = categories.includes(searchParams.get("category")) ? searchParams.get("category") : categories[0];
    const allFunctions = getFilteredFunctions();
    const functions = getFilteredFunctions(query)
        .filter(func => category === categories[0] || getFunctionCategory(func) === category)
        .sort((a, b) => a.Name.localeCompare(b.Name));
    const updateFilter = (name, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(name, value); else next.delete(name);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="catalog-page" data-view={view}>
            <PageHeading eyebrow="Engineering utilities" title="Function catalog"
                description="Published functions, ready to inspect and call. Requests use your signed-in permissions."
                actions={<><ViewControls /><Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={refreshFunctions} disabled={isLoading}>Refresh</Button></>} />
            <section aria-label="Published functions" className="catalog-section">
                <div className="category-filters" role="group" aria-label="Function categories">
                    {categories.map(item => (
                        <button key={item} className={`category-filter${category === item ? " selected" : ""}`}
                            aria-pressed={category === item} onClick={() => updateFilter("category", item === categories[0] ? "" : item)}>
                            {item}
                        </button>
                    ))}
                </div>
                <div className="catalog-toolbar">
                    <Input contentBefore={<SearchRegular />} value={query} onChange={(_, data) => updateFilter("q", data.value)}
                        contentAfter={query && <Button size="small" appearance="transparent" icon={<DismissRegular />} aria-label="Clear search" onClick={() => updateFilter("q", "")} />}
                        placeholder="Filter by name or route..." aria-label="Search function catalog" className="catalog-search" />
                    <span className="catalog-result-count" aria-live="polite">
                        {!isLoading && !error && <><strong>{functions.length}</strong> of {allFunctions.length} functions</>}
                    </span>
                </div>
                {isLoading ? <PageState loading title="Loading the function catalog..." /> :
                    error ? <PageState error title="The catalog is unavailable" action={<Button onClick={refreshFunctions}>Try again</Button>}>{error.message}</PageState> :
                    functions.length === 0 ? <PageState title={allFunctions.length ? "No matching functions" : "No published functions"}
                        action={allFunctions.length > 0 && <Button onClick={() => setSearchParams({})}>Clear filters</Button>}>
                        {allFunctions.length ? "Try another keyword or category." : "Functions will appear here when published by the configured endpoint."}
                    </PageState> :
                    view === "compact" ? <CompactLinkList label="Published function names" items={functions.map(func => ({
                        name: func.Name, to: `/${encodeURIComponent(func.Name)}`,
                        description: func.Description || `/api/${func.Route || func.Name}`,
                    }))} /> : <div className="registry-scroll">
                        <table className="function-registry">
                            <caption className="visually-hidden">Published functions. Select a function to configure its request.</caption>
                            <thead><tr><th scope="col">Function</th><th scope="col">Method</th><th scope="col">API route</th></tr></thead>
                            <tbody>
                                {functions.map(func => (
                                    <tr key={func.Name}>
                                        <td>
                                            <Link to={`/${encodeURIComponent(func.Name)}`} className="registry-function">
                                                <h2>{func.Name}<ArrowRightRegular aria-hidden="true" /></h2>
                                                {func.Description && <p>{func.Description}</p>}
                                            </Link>
                                        </td>
                                        <td className="registry-methods">{func.Methods?.length
                                            ? func.Methods.map(method => <span key={method} className="http-method">{method.toUpperCase()}</span>)
                                            : <span className="registry-unspecified">Not listed</span>}</td>
                                        <td className="registry-route"><code>/api/{func.Route || func.Name}</code></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>}
                <p className="catalog-note">Select a function to review its parameters. Nothing runs until you send a request.</p>
            </section>
        </div>
    );
};
